/**
 * Calcoli statistici sulle news caricate.
 *
 * Funzioni pure: ricevono un array di story normalizzate e restituiscono
 * numeri. Nessun accesso al DOM, nessuna dipendenza esterna — sono il cuore
 * "calcolato" dell'applicazione e come tali sono coperte dai test.
 */

/**
 * Aggrega le metriche mostrate nella barra delle statistiche.
 *
 * @param {Array<{score:number, comments:number, time:number}>} stories
 * @returns {{ total: number, avgScore: number, totalComments: number, freshest: number|null }}
 */
export function computeStats(stories = []) {
  const list = Array.isArray(stories) ? stories : [];

  if (list.length === 0) {
    return { total: 0, avgScore: 0, totalComments: 0, freshest: null };
  }

  let scoreSum = 0;
  let totalComments = 0;
  let freshest = 0;

  for (const story of list) {
    scoreSum += Number(story?.score) || 0;
    totalComments += Number(story?.comments) || 0;
    const time = Number(story?.time) || 0;
    if (time > freshest) freshest = time;
  }

  return {
    total: list.length,
    avgScore: Math.round((scoreSum / list.length) * 10) / 10,
    totalComments,
    freshest: freshest || null,
  };
}

/**
 * Classifica dei domini più ricorrenti tra le news caricate.
 *
 * @param {Array<{domain: string}>} stories
 * @param {number} [limit=5]
 * @returns {Array<{ domain: string, count: number, percentage: number }>}
 *          ordinata per occorrenze decrescenti; `percentage` è relativa
 *          al dominio più citato (serve a dimensionare le barre).
 */
export function getTopDomains(stories = [], limit = 5) {
  const list = Array.isArray(stories) ? stories : [];
  const counts = new Map();

  for (const story of list) {
    const domain = story?.domain;
    if (!domain) continue;
    counts.set(domain, (counts.get(domain) ?? 0) + 1);
  }

  const ranked = [...counts.entries()]
    .map(([domain, count]) => ({ domain, count }))
    // A parità di occorrenze ordina alfabeticamente, così l'output è stabile.
    .sort((a, b) => b.count - a.count || a.domain.localeCompare(b.domain))
    .slice(0, Math.max(0, limit));

  const max = ranked[0]?.count ?? 0;

  return ranked.map((entry) => ({
    ...entry,
    percentage: max > 0 ? Math.round((entry.count / max) * 100) : 0,
  }));
}

/**
 * Formatta un numero in forma compatta (1200 → "1,2k").
 *
 * @param {number} value
 * @returns {string}
 */
export function formatCompact(value) {
  const number = Number(value) || 0;
  if (Math.abs(number) < 1000) return String(number);
  const short = Math.round((number / 1000) * 10) / 10;
  return `${String(short).replace('.', ',')}k`;
}
